import dayjs from 'dayjs';
import mapValues from 'lodash/mapValues';
import meanBy from 'lodash/meanBy';
import slice from 'lodash/slice';

import { AVERAGE_HOURS_IN_MONTH, HOURS_IN_DAY } from '@cast/constants';
import type { DateRange, PriceType } from '@cast/shared-types';
import { WorkloadCostMetric } from '@cast/types';
import { dateRangeIterator, toNumOrZero, toBig } from '@cast/utils';

import {
  AggregatedMetrics,
  ComputeSpendChartData,
  CostReportChartDataByPeriod,
  CostReportDataByPeriod,
  HourlyCostPerResource,
  UptimeWorkloadCostReportData,
} from 'features/cost-report/types/costOverTime';
import { WorkloadCostReport } from 'features/cost-report/types/workloadsCostReport';
import { getUptimeMultiplier } from 'features/cost-report/utils/costOverTime';
import { aggregateCostOverTimeMetrics } from 'features/cost-report/utils/metrics/aggregateCostOverTimeMetrics';
import { compensateDSTv2 } from 'utils/compensateDST';
import { fillGaps } from 'utils/metrics';

import { makeCumulativeReducerFn } from '../../../utils';

const emptyDataPoint: Omit<
  UptimeWorkloadCostReportData,
  'timestamp' | 'generated' | 'forecast'
> = {
  totalCpu: 0,
  totalGpu: 0,
  totalRamGib: 0,
  totalCost: 0,
  totalCpuCost: 0,
  totalGpuCost: 0,
  totalRamCost: 0,
  normalizedCostPerCpu: 0,
  normalizedCostPerGpu: 0,
  normalizedCostPerGib: 0,

  onDemandCost: 0,
  onDemandCostPerCpu: 0,
  onDemandCostPerGib: 0,
  onDemandCpuCount: 0,
  onDemandUptime: 0,
  onDemandCpuCost: 0,
  onDemandRamCost: 0,
  onDemandRamGib: 0,
  onDemandCostPerGpu: 0,
  onDemandGpuCount: 0,
  onDemandGpuCost: 0,

  fallbackCost: 0,
  fallbackCostPerCpu: 0,
  fallbackCostPerGib: 0,
  fallbackCpuCount: 0,
  fallbackUptime: 0,
  fallbackCpuCost: 0,
  fallbackRamCost: 0,
  fallbackRamGib: 0,
  fallbackCostPerGpu: 0,
  fallbackGpuCount: 0,
  fallbackGpuCost: 0,

  spotCost: 0,
  spotCostPerCpu: 0,
  spotCostPerGib: 0,
  spotCpuCount: 0,
  spotUptime: 0,
  spotCpuCost: 0,
  spotRamCost: 0,
  spotRamGib: 0,
  spotCostPerGpu: 0,
  spotGpuCount: 0,
  spotGpuCost: 0,

  totalTpu: 0,
  totalTpuCost: 0,
  normalizedCostPerTpu: 0,
  onDemandCostPerTpu: 0,
  onDemandTpuCount: 0,
  onDemandTpuCost: 0,
  fallbackCostPerTpu: 0,
  fallbackTpuCount: 0,
  fallbackTpuCost: 0,
  spotCostPerTpu: 0,
  spotTpuCount: 0,
  spotTpuCost: 0,

  onDemandPodCount: 0,
  spotPodCount: 0,
  fallbackPodCount: 0,
  onDemandCostPerPod: 0,
  spotCostPerPod: 0,
  fallbackCostPerPod: 0,
  totalPods: 0,
  normalizedCostPerPod: 0,

  costPerStorage: 0,
  totalStorage: 0,
  totalStorageCost: 0,
  normalizedCostPerStorage: 0,
};

export const transformWorkloadCostData = (
  [startTime, endTime]: DateRange,
  data: WorkloadCostReport | undefined,
  timezone: string
): [UptimeWorkloadCostReportData[], CostReportChartDataByPeriod[]] => {
  const dataByKey: Record<
    UptimeWorkloadCostReportData['timestamp'],
    UptimeWorkloadCostReportData
  > = {};

  if (data) {
    for (const metric of data.costMetrics) {
      const {
        timestamp,
        costOnDemand,
        costSpot,
        costSpotFallback,
        cpuCountOnDemand,
        cpuCountSpot,
        cpuCountSpotFallback,
        cpuCostOnDemand,
        cpuCostSpot,
        cpuCostSpotFallback,
        gpuCountOnDemand,
        gpuCountSpot,
        gpuCountSpotFallback,
        gpuCostOnDemand,
        gpuCostSpot,
        gpuCostSpotFallback,
        tpuCountOnDemand,
        tpuCountSpot,
        tpuCountSpotFallback,
        tpuCostOnDemand,
        tpuCostSpot,
        tpuCostSpotFallback,
        ramGibOnDemand,
        ramGibSpot,
        ramGibSpotFallback,
        ramCostOnDemand,
        ramCostSpot,
        ramCostSpotFallback,
        podCountOnDemand,
        podCountSpot,
        podCountSpotFallback,
        uptimeMinutesOnDemand,
        uptimeMinutesSpot,
        uptimeMinutesSpotFallback,
        storageCost: storageCostDaily,
        storageGib,
      }: WorkloadCostMetric = metric;

      // The endpoint reports a daily storage cost,
      // remaining fields report it hourly
      const storageCost = toNumOrZero(storageCostDaily) / HOURS_IN_DAY;

      const localTimestamp = dayjs.utc(timestamp).toISOString();

      const onDemandUptime = parseFloat(uptimeMinutesOnDemand || '0');
      const fallbackUptime = parseFloat(uptimeMinutesSpotFallback || '0');
      const spotUptime = parseFloat(uptimeMinutesSpot || '0');

      const onDemandCost = toBig(costOnDemand || 0).toNumber();
      const fallbackCost = toBig(costSpotFallback || 0).toNumber();
      const spotCost = toBig(costSpot || 0).toNumber();

      const totalCpu = toBig(cpuCountOnDemand || 0)
        .add(cpuCountSpotFallback || 0)
        .add(cpuCountSpot || 0)
        .toNumber();
      const totalGpu = toBig(gpuCountOnDemand || 0)
        .add(gpuCountSpotFallback || 0)
        .add(gpuCountSpot || 0)
        .toNumber();
      const totalTpu = toBig(tpuCountOnDemand || 0)
        .add(tpuCountSpotFallback || 0)
        .add(tpuCountSpot || 0)
        .toNumber();
      const totalRamGib = toBig(ramGibOnDemand || 0)
        .add(ramGibSpotFallback || 0)
        .add(ramGibSpot || 0)
        .toNumber();
      const totalCost = toBig(onDemandCost || 0)
        .add(fallbackCost || 0)
        .add(spotCost || 0)
        .add(storageCost || 0)
        .toNumber();
      const totalCpuCost = toBig(cpuCostOnDemand || 0)
        .add(cpuCostSpotFallback || 0)
        .add(cpuCostSpot || 0)
        .toNumber();
      const totalGpuCost = toBig(gpuCostOnDemand || 0)
        .add(gpuCostSpotFallback || 0)
        .add(gpuCostSpot || 0)
        .toNumber();
      const totalTpuCost = toBig(tpuCostOnDemand || 0)
        .add(tpuCostSpotFallback || 0)
        .add(tpuCostSpot || 0)
        .toNumber();
      const totalRamCost = toBig(ramCostOnDemand || 0)
        .add(ramCostSpotFallback || 0)
        .add(ramCostSpot || 0)
        .toNumber();
      const totalPods = toBig(podCountOnDemand || 0)
        .add(podCountSpotFallback || 0)
        .add(podCountSpot || 0)
        .toNumber();

      const onDemandCpuCost = parseFloat(cpuCostOnDemand || '0');
      const fallbackCpuCost = parseFloat(cpuCostSpotFallback || '0');
      const spotCpuCost = parseFloat(cpuCostSpot || '0');

      const onDemandGpuCost = parseFloat(gpuCostOnDemand || '0');
      const fallbackGpuCost = parseFloat(gpuCostSpotFallback || '0');
      const spotGpuCost = parseFloat(gpuCostSpot || '0');

      const onDemandTpuCost = parseFloat(tpuCostOnDemand || '0');
      const fallbackTpuCost = parseFloat(tpuCostSpotFallback || '0');
      const spotTpuCost = parseFloat(tpuCostSpot || '0');

      const onDemandRamCost = parseFloat(ramCostOnDemand || '0');
      const fallbackRamCost = parseFloat(ramCostSpotFallback || '0');
      const spotRamCost = parseFloat(ramCostSpot || '0');

      const onDemandCpuCount = parseFloat(cpuCountOnDemand || '0');
      const fallbackCpuCount = parseFloat(cpuCountSpotFallback || '0');
      const spotCpuCount = parseFloat(cpuCountSpot || '0');

      const onDemandGpuCount = parseFloat(gpuCountOnDemand || '0');
      const fallbackGpuCount = parseFloat(gpuCountSpotFallback || '0');
      const spotGpuCount = parseFloat(gpuCountSpot || '0');

      const onDemandTpuCount = parseFloat(tpuCountOnDemand || '0');
      const fallbackTpuCount = parseFloat(tpuCountSpotFallback || '0');
      const spotTpuCount = parseFloat(tpuCountSpot || '0');

      const onDemandRamGib = parseFloat(ramGibOnDemand || '0');
      const fallbackRamGib = parseFloat(ramGibSpotFallback || '0');
      const spotRamGib = parseFloat(ramGibSpot || '0');

      const onDemandPodCount = parseFloat(podCountOnDemand || '0');
      const fallbackPodCount = parseFloat(podCountSpotFallback || '0');
      const spotPodCount = parseFloat(podCountSpot || '0');

      const normalizedCostPerCpu = totalCpu ? totalCpuCost / totalCpu : 0;
      const onDemandCostPerCpu = onDemandCpuCount
        ? toBig(onDemandCpuCost).div(onDemandCpuCount).toNumber()
        : 0;
      const fallbackCostPerCpu = fallbackCpuCount
        ? toBig(fallbackCpuCost).div(fallbackCpuCount).toNumber()
        : 0;
      const spotCostPerCpu = spotCpuCount
        ? toBig(spotCpuCost).div(spotCpuCount).toNumber()
        : 0;

      const normalizedCostPerGpu = totalGpu ? totalGpuCost / totalGpu : 0;
      const onDemandCostPerGpu = onDemandGpuCount
        ? toBig(onDemandGpuCost).div(onDemandGpuCount).toNumber()
        : 0;
      const fallbackCostPerGpu = fallbackGpuCount
        ? toBig(fallbackGpuCost).div(fallbackGpuCount).toNumber()
        : 0;
      const spotCostPerGpu = spotGpuCount
        ? toBig(spotGpuCost).div(spotGpuCount).toNumber()
        : 0;

      const normalizedCostPerTpu = totalTpu ? totalTpuCost / totalTpu : 0;
      const onDemandCostPerTpu = onDemandTpuCount
        ? toBig(onDemandTpuCost).div(onDemandTpuCount).toNumber()
        : 0;
      const fallbackCostPerTpu = fallbackTpuCount
        ? toBig(fallbackTpuCost).div(fallbackTpuCount).toNumber()
        : 0;
      const spotCostPerTpu = spotTpuCount
        ? toBig(spotTpuCost).div(spotTpuCount).toNumber()
        : 0;

      const normalizedCostPerGib = totalCpu ? totalRamCost / totalRamGib : 0;
      const onDemandCostPerGib = onDemandRamGib
        ? toBig(onDemandRamCost).div(onDemandRamGib).toNumber()
        : 0;
      const fallbackCostPerGib = fallbackRamGib
        ? toBig(fallbackRamCost).div(fallbackRamGib).toNumber()
        : 0;
      const spotCostPerGib = spotRamGib
        ? toBig(spotRamCost).div(spotRamGib).toNumber()
        : 0;

      const normalizedCostPerPod = totalPods ? totalCost / totalPods : 0;

      const onDemandCostPerPod = onDemandPodCount
        ? onDemandCost / onDemandPodCount
        : 0;
      const fallbackCostPerPod = fallbackPodCount
        ? fallbackCost / fallbackPodCount
        : 0;
      const spotCostPerPod = spotPodCount ? spotCost / spotPodCount : 0;

      const totalStorage = toBig(storageGib || 0).toNumber();
      const totalStorageCost = toBig(storageCost || 0).toNumber();
      const costPerStorage = totalStorage
        ? toBig(totalStorageCost).div(totalStorage).toNumber()
        : 0;

      if (localTimestamp in dataByKey) {
        dataByKey[localTimestamp] = {
          ...dataByKey[localTimestamp],
          fallbackCpuCost:
            dataByKey[localTimestamp].fallbackCpuCost + fallbackCpuCost,
          fallbackGpuCost:
            dataByKey[localTimestamp].fallbackGpuCost + fallbackGpuCost,
          fallbackTpuCost:
            dataByKey[localTimestamp].fallbackTpuCost + fallbackTpuCost,
          fallbackRamCost:
            dataByKey[localTimestamp].fallbackRamCost + fallbackRamCost,
          onDemandCpuCost:
            dataByKey[localTimestamp].onDemandCpuCost + onDemandCpuCost,
          onDemandGpuCost:
            dataByKey[localTimestamp].onDemandGpuCost + onDemandGpuCost,
          onDemandTpuCost:
            dataByKey[localTimestamp].onDemandTpuCost + onDemandTpuCost,
          onDemandRamCost:
            dataByKey[localTimestamp].onDemandRamCost + onDemandRamCost,
          spotCpuCost: dataByKey[localTimestamp].spotCpuCost + spotCpuCost,
          spotGpuCost: dataByKey[localTimestamp].spotGpuCost + spotGpuCost,
          spotTpuCost: dataByKey[localTimestamp].spotTpuCost + spotTpuCost,
          spotRamCost: dataByKey[localTimestamp].spotRamCost + spotRamCost,
          totalCpu: dataByKey[localTimestamp].totalCpu + totalCpu,
          totalGpu: dataByKey[localTimestamp].totalGpu + totalGpu,
          totalTpu: dataByKey[localTimestamp].totalTpu + totalTpu,
          totalRamGib: dataByKey[localTimestamp].totalRamGib + totalRamGib,
          totalCost: dataByKey[localTimestamp].totalCost + totalCost,
          totalCpuCost: dataByKey[localTimestamp].totalCpuCost + totalCpuCost,
          totalGpuCost: dataByKey[localTimestamp].totalGpuCost + totalGpuCost,
          totalTpuCost: dataByKey[localTimestamp].totalTpuCost + totalTpuCost,
          totalRamCost: dataByKey[localTimestamp].totalRamCost + totalRamCost,
          totalPods: dataByKey[localTimestamp].totalPods + totalPods,
          normalizedCostPerCpu:
            dataByKey[localTimestamp].normalizedCostPerCpu +
            normalizedCostPerCpu,
          normalizedCostPerGib:
            dataByKey[localTimestamp].normalizedCostPerGib +
            normalizedCostPerGib,
          normalizedCostPerGpu:
            dataByKey[localTimestamp].normalizedCostPerGpu +
            normalizedCostPerGpu,
          normalizedCostPerTpu:
            dataByKey[localTimestamp].normalizedCostPerTpu +
            normalizedCostPerTpu,
          normalizedCostPerPod:
            dataByKey[localTimestamp].normalizedCostPerPod +
            normalizedCostPerPod,
          onDemandCost: dataByKey[localTimestamp].onDemandCost + onDemandCost,
          onDemandCostPerCpu:
            dataByKey[localTimestamp].onDemandCostPerCpu + onDemandCostPerCpu,
          onDemandCostPerGpu:
            dataByKey[localTimestamp].onDemandCostPerGpu + onDemandCostPerGpu,
          onDemandCostPerTpu:
            dataByKey[localTimestamp].onDemandCostPerTpu + onDemandCostPerTpu,
          onDemandCostPerGib:
            dataByKey[localTimestamp].onDemandCostPerGib + onDemandCostPerGib,
          onDemandCpuCount:
            dataByKey[localTimestamp].onDemandCpuCount + onDemandCpuCount,
          onDemandGpuCount:
            dataByKey[localTimestamp].onDemandGpuCount + onDemandGpuCount,
          onDemandTpuCount:
            dataByKey[localTimestamp].onDemandTpuCount + onDemandTpuCount,
          onDemandRamGib:
            dataByKey[localTimestamp].onDemandRamGib + onDemandRamGib,
          onDemandCostPerPod:
            dataByKey[localTimestamp].onDemandCostPerPod + onDemandCostPerPod,
          onDemandPodCount:
            dataByKey[localTimestamp].onDemandPodCount + onDemandPodCount,
          onDemandUptime:
            dataByKey[localTimestamp].onDemandUptime + onDemandUptime,
          fallbackCost: dataByKey[localTimestamp].fallbackCost + fallbackCost,
          fallbackCostPerCpu:
            dataByKey[localTimestamp].fallbackCostPerCpu + fallbackCostPerCpu,
          fallbackCostPerGpu:
            dataByKey[localTimestamp].fallbackCostPerGpu + fallbackCostPerGpu,
          fallbackCostPerTpu:
            dataByKey[localTimestamp].fallbackCostPerTpu + fallbackCostPerTpu,
          fallbackCostPerGib:
            dataByKey[localTimestamp].fallbackCostPerGib + fallbackCostPerGib,
          fallbackCpuCount:
            dataByKey[localTimestamp].fallbackCpuCount + fallbackCpuCount,
          fallbackGpuCount:
            dataByKey[localTimestamp].fallbackGpuCount + fallbackGpuCount,
          fallbackTpuCount:
            dataByKey[localTimestamp].fallbackTpuCount + fallbackTpuCount,
          fallbackRamGib:
            dataByKey[localTimestamp].fallbackRamGib + fallbackRamGib,
          fallbackCostPerPod:
            dataByKey[localTimestamp].fallbackCostPerPod + fallbackCostPerPod,
          fallbackPodCount:
            dataByKey[localTimestamp].fallbackPodCount + fallbackPodCount,
          fallbackUptime:
            dataByKey[localTimestamp].fallbackUptime + fallbackUptime,
          spotCost: dataByKey[localTimestamp].spotCost + spotCost,
          spotCostPerCpu:
            dataByKey[localTimestamp].spotCostPerCpu + spotCostPerCpu,
          spotCostPerGpu:
            dataByKey[localTimestamp].spotCostPerGpu + spotCostPerGpu,
          spotCostPerTpu:
            dataByKey[localTimestamp].spotCostPerTpu + spotCostPerTpu,
          spotCostPerGib:
            dataByKey[localTimestamp].spotCostPerGib + spotCostPerGib,
          spotCpuCount: dataByKey[localTimestamp].spotCpuCount + spotCpuCount,
          spotGpuCount: dataByKey[localTimestamp].spotGpuCount + spotGpuCount,
          spotTpuCount: dataByKey[localTimestamp].spotTpuCount + spotTpuCount,
          spotRamGib: dataByKey[localTimestamp].spotRamGib + spotRamGib,
          spotPodCount: dataByKey[localTimestamp].spotPodCount + spotPodCount,
          spotCostPerPod:
            dataByKey[localTimestamp].spotCostPerPod + spotCostPerPod,
          spotUptime: dataByKey[localTimestamp].spotUptime + spotUptime,
          costPerStorage:
            dataByKey[localTimestamp].costPerStorage + costPerStorage,
          totalStorage: dataByKey[localTimestamp].totalStorage + totalStorage,
          totalStorageCost:
            dataByKey[localTimestamp].totalStorageCost + totalStorageCost,
          normalizedCostPerStorage:
            dataByKey[localTimestamp].normalizedCostPerStorage + totalStorage,
        };
      } else {
        dataByKey[localTimestamp] = {
          ...emptyDataPoint,
          timestamp: localTimestamp,
          generated: false,
          forecast: false,
          totalCpu,
          totalGpu,
          totalRamGib,
          totalCost,
          totalCpuCost,
          totalGpuCost,
          totalRamCost,
          totalPods,
          normalizedCostPerCpu,
          normalizedCostPerGpu,
          normalizedCostPerGib,
          normalizedCostPerPod,
          onDemandCost,
          onDemandCostPerCpu,
          onDemandCostPerGpu,
          onDemandCostPerGib,
          onDemandCpuCount,
          onDemandGpuCount,
          onDemandCostPerPod,
          onDemandPodCount,
          onDemandUptime,
          fallbackCost,
          fallbackCostPerCpu,
          fallbackCostPerGpu,
          fallbackCostPerGib,
          fallbackCpuCount,
          fallbackGpuCount,
          fallbackCostPerPod,
          fallbackPodCount,
          fallbackUptime,
          spotCost,
          spotCostPerCpu,
          spotCostPerGpu,
          spotCostPerGib,
          spotCpuCount,
          spotGpuCount,
          spotPodCount,
          spotCostPerPod,
          spotUptime,
          fallbackCpuCost,
          fallbackGpuCost,
          fallbackTpuCost,
          fallbackRamCost,
          fallbackRamGib,
          onDemandCpuCost,
          onDemandGpuCost,
          onDemandTpuCost,
          onDemandRamCost,
          onDemandRamGib,
          spotCpuCost,
          spotGpuCost,
          spotTpuCost,
          spotRamCost,
          spotRamGib,
          totalTpu,
          totalTpuCost,
          normalizedCostPerTpu,
          onDemandCostPerTpu,
          onDemandTpuCount,
          fallbackCostPerTpu,
          fallbackTpuCount,
          spotCostPerTpu,
          spotTpuCount,
          costPerStorage,
          totalStorage,
          totalStorageCost,
          normalizedCostPerStorage: totalStorage,
        };
      }
    }

    const existingTimestamps: string[] = Object.keys(dataByKey);
    const firstFutureTimestamp = dayjs().tz(timezone).startOf('d').add(1, 'd');
    const applyForecast = endTime.isAfter(firstFutureTimestamp);

    // generate moving average
    if (applyForecast) {
      const skipKeys: string[] = [];

      if (existingTimestamps.length > 1) {
        skipKeys.push(firstFutureTimestamp.toISOString());
      }

      for (const timestamp of dateRangeIterator(
        firstFutureTimestamp,
        endTime.subtract(1, 'day')
      )) {
        const _data = existingTimestamps.filter(
          (timestamp) => !skipKeys.includes(timestamp)
        );
        const _windowTimestamps = slice(_data, Math.max(0, _data.length - 7));
        const _window = _windowTimestamps.map((t) => dataByKey[t]);

        const onDemandCost = meanBy(_window, 'onDemandCost') || 0;
        const onDemandCpuCost = meanBy(_window, 'onDemandCpuCost') || 0;
        const onDemandRamCost = meanBy(_window, 'onDemandRamCost') || 0;
        const onDemandCpuCount = meanBy(_window, 'onDemandCpuCount') || 0;
        const onDemandRamGib =
          meanBy(_window, (row) => row.onDemandRamGib) || 0;
        const onDemandPodCount = meanBy(_window, 'onDemandPodCount') || 0;
        const onDemandUptime = meanBy(_window, 'onDemandUptime') || 0;

        const fallbackCost = meanBy(_window, 'fallbackCost') || 0;
        const fallbackCpuCost = meanBy(_window, 'fallbackCpuCost') || 0;
        const fallbackRamCost = meanBy(_window, 'fallbackRamCost') || 0;
        const fallbackCount = meanBy(_window, 'fallbackCount') || 0;
        const fallbackRamGib =
          meanBy(_window, (row) => row.fallbackRamGib) || 0;
        const fallbackPodCount = meanBy(_window, 'fallbackPodCount') || 0;

        const spotCost = meanBy(_window, 'spotCost') + fallbackCost / 2 || 0;
        const spotCpuCost =
          meanBy(_window, 'spotCpuCost') + fallbackCpuCost / 2 || 0;
        const spotRamCost =
          meanBy(_window, 'spotRamCost') + fallbackRamCost / 2 || 0;
        const spotCpuCount =
          meanBy(_window, 'spotCpuCount') + fallbackCount || 0;
        const spotRamGib =
          meanBy(_window, (row) => row.spotRamGib) + fallbackCount || 0;
        const spotPodCount =
          meanBy(_window, 'spotPodCount') + fallbackPodCount || 0;
        const spotUptime = meanBy(_window, 'spotUptime') || 0;
        const totalStorage = meanBy(_window, 'totalStorage') || 0;
        const totalStorageCost = meanBy(_window, 'totalStorageCost') || 0;
        const costPerStorage = meanBy(_window, 'costPerStorage') || 0;

        const totalCpu = onDemandCpuCount + fallbackCount + spotCpuCount;
        const totalRamGib = onDemandRamGib + fallbackRamGib + spotRamGib;
        const totalCost = onDemandCost + fallbackCost + spotCost;
        const totalCpuCost = onDemandCpuCost + fallbackCpuCost + spotCpuCost;
        const totalRamCost = onDemandRamCost + fallbackRamCost + spotRamCost;
        const totalPods = onDemandPodCount + fallbackPodCount + spotPodCount;

        const normalizedCostPerCpu = totalCpu ? totalCpuCost / totalCpu : 0;
        const onDemandCostPerCpu = onDemandCpuCount
          ? onDemandCpuCost / onDemandCpuCount
          : 0;
        const spotCostPerCpu = spotCpuCount ? spotCpuCost / spotCpuCount : 0;

        const normalizedCostPerGib = totalRamGib
          ? totalRamCost / totalRamGib
          : 0;
        const onDemandCostPerGib = onDemandRamGib
          ? onDemandRamCost / onDemandRamGib
          : 0;
        const spotCostPerGib = spotRamGib ? spotRamCost / spotRamGib : 0;

        const normalizedCostPerPod = totalPods ? totalCost / totalPods : 0;
        const onDemandCostPerPod = onDemandPodCount
          ? onDemandCost / onDemandPodCount
          : 0;
        const spotCostPerPod = spotPodCount ? spotCost / spotPodCount : 0;

        const dateTimestamp = timestamp.toISOString();

        existingTimestamps.push(dateTimestamp);

        dataByKey[dateTimestamp] = {
          ...emptyDataPoint,
          timestamp: dateTimestamp,
          forecast: true,
          generated: false,
          totalCpu,
          totalRamGib,
          totalCost,
          totalPods,
          normalizedCostPerCpu,
          normalizedCostPerGib,
          normalizedCostPerPod,
          onDemandCost,
          onDemandCostPerCpu,
          onDemandCostPerGib,
          onDemandCpuCount,
          onDemandCostPerPod,
          onDemandPodCount,
          onDemandUptime,
          fallbackCost: 0,
          fallbackCostPerCpu: 0,
          fallbackCpuCount: 0,
          fallbackCostPerPod: 0,
          fallbackPodCount: 0,
          fallbackUptime: 0,
          fallbackCostPerGib: 0,
          fallbackCpuCost: 0,
          fallbackRamCost: 0,
          fallbackRamGib: 0,
          spotCost,
          spotCostPerCpu,
          spotCostPerGib,
          spotCpuCount,
          spotPodCount,
          spotCostPerPod,
          spotUptime,
          onDemandCpuCost,
          onDemandRamCost,
          onDemandRamGib,
          spotCpuCost,
          spotRamCost,
          spotRamGib,
          totalCpuCost,
          totalRamCost,
          costPerStorage,
          totalStorage,
          totalStorageCost,
          normalizedCostPerStorage: totalStorage,
        };
      }
    }
  }

  const result: CostReportChartDataByPeriod[] = [];

  const chartStartTime = startTime;
  const chartEndTime = endTime.subtract(1, 'day');

  for (const timestamp of dateRangeIterator(chartStartTime, chartEndTime)) {
    const dateTimestamp = timestamp.toISOString();
    if (dateTimestamp in dataByKey) {
      result.push(dataByKey[dateTimestamp]);
    }
  }

  const chartData = compensateDSTv2(
    fillGaps({
      timeSeries: result,
      dateRange: {
        from: chartStartTime,
        to: chartEndTime,
      },
      filler: { generated: true, ...mapValues(emptyDataPoint, () => null) },
      unit: 'day',
      step: 1,
    }),
    timezone
  );

  return [Object.values(dataByKey), chartData];
};

const calculateMonthlyForecast = <
  T extends CostReportDataByPeriod | UptimeWorkloadCostReportData
>(
  data: T[]
): number => {
  const cumulativeData = data.reduce(
    (
      acc: ComputeSpendChartData[],
      datapoint: CostReportDataByPeriod,
      index: number
    ) => {
      const reducerFn = makeCumulativeReducerFn(getUptimeMultiplier(datapoint));
      return reducerFn(acc, datapoint, index);
    },
    []
  );
  const lastDatapoint = cumulativeData.pop();
  return (
    (lastDatapoint?.onDemand ?? 0) +
    (lastDatapoint?.fallback ?? 0) +
    (lastDatapoint?.spot ?? 0)
  );
};

type AggregateClusterMetricsArgs<T> = {
  isDefaultFilter: boolean;
  currentData: T[];
  previousData: T[];
  storageCostGranularity: PriceType;
  timezone: string;
};

export const aggregateClusterMetrics = <
  T extends CostReportDataByPeriod | UptimeWorkloadCostReportData
>({
  isDefaultFilter,
  currentData,
  previousData,
  storageCostGranularity,
  timezone,
}: AggregateClusterMetricsArgs<T>): AggregatedMetrics => {
  const current = aggregateCostOverTimeMetrics(
    currentData,
    timezone,
    storageCostGranularity
  );
  const previous = aggregateCostOverTimeMetrics(
    previousData,
    timezone,
    storageCostGranularity
  );

  const computeCost = {
    hourly: current.average.totalCost,
    daily: current.uptimeOngoing.total,
    total: current.uptimeTotal.total,
    todayProjected: current.uptimeOngoing.total,
    onDemandProjected: current.uptimeOngoing.onDemand,
    fallbackProjected: current.uptimeOngoing.fallback,
    spotProjected: current.uptimeOngoing.spot,
    storageProjected: current.uptimeOngoing.storage,
    rangeSoFar: current.uptimeTotal.total,
    onDemandRangeSoFar: current.uptimeTotal.onDemand,
    fallbackRangeSoFar: current.uptimeTotal.fallback,
    spotRangeSoFar: current.uptimeTotal.spot,
    storageRangeSoFar: current.uptimeTotal.storage,
  };

  const hourlyCostPerCpu: HourlyCostPerResource = {
    normalizedSoFar: current.ongoing.normalizedCostPerCpu,
    normalizedAverage: current.average.normalizedCostPerCpu,

    onDemandSoFar: current.ongoing.onDemandCostPerCpu,
    onDemandAverage: current.average.onDemandCostPerCpu,

    fallbackSoFar: current.ongoing.fallbackCostPerCpu,
    fallbackAverage: current.average.onDemandCostPerCpu,

    spotSoFar: current.ongoing.spotCostPerCpu,
    spotAverage: current.average.onDemandCostPerCpu,
  };

  const hourlyCostPerRamGib: HourlyCostPerResource = {
    normalizedSoFar: current.ongoing.normalizedCostPerGib,
    normalizedAverage: current.average.normalizedCostPerGib,

    onDemandSoFar: current.ongoing.onDemandCostPerGib,
    onDemandAverage: current.average.onDemandCostPerGib,

    fallbackSoFar: current.ongoing.fallbackCostPerGib,
    fallbackAverage: current.average.onDemandCostPerGib,

    spotSoFar: current.ongoing.spotCostPerGib,
    spotAverage: current.average.onDemandCostPerGib,
  };

  const hourlyCostPerGpu: HourlyCostPerResource = {
    normalizedSoFar: current.ongoing.normalizedCostPerGpu,
    normalizedAverage: current.average.normalizedCostPerGpu,

    onDemandSoFar: current.ongoing.onDemandCostPerGpu,
    onDemandAverage: current.average.onDemandCostPerGpu,

    fallbackSoFar: current.ongoing.fallbackCostPerGpu,
    fallbackAverage: current.average.onDemandCostPerGpu,

    spotSoFar: current.ongoing.spotCostPerGpu,
    spotAverage: current.average.onDemandCostPerGpu,
  };

  const hourlyCostPerStorage: HourlyCostPerResource = {
    normalizedSoFar: current.ongoing.costPerStorage,
    normalizedAverage: current.average.costPerStorage,

    onDemandSoFar: 0,
    onDemandAverage: 0,

    fallbackSoFar: 0,
    fallbackAverage: 0,

    spotSoFar: 0,
    spotAverage: 0,
  };

  const podsCount = {
    total:
      current.ongoing.onDemandPodCount +
      current.ongoing.fallbackPodCount +
      current.ongoing.spotPodCount,
    onDemand: current.ongoing.onDemandPodCount,
    fallback: current.ongoing.fallbackPodCount,
    spot: current.ongoing.spotPodCount,
  };

  const podsCost = {
    normalized: current.ongoing.normalizedCostPerPod,
    onDemand: current.ongoing.onDemandCostPerPod,
    fallback: current.ongoing.fallbackCostPerPod,
    spot: current.ongoing.spotCostPerPod,
  };

  return {
    computeCost,
    hourlyCostPerCpu,
    hourlyCostPerGpu,
    hourlyCostPerRamGib,
    hourlyCostPerStorage,
    podsCount,
    podsCost,

    cpuCount: {
      total:
        current.average.onDemandCpuCount +
        current.average.fallbackCpuCount +
        current.average.spotCpuCount,
      onDemand: current.average.onDemandCpuCount,
      fallback: current.average.fallbackCpuCount,
      spot: current.average.spotCpuCount,
    },

    monthlyForecast: {
      cost: isDefaultFilter
        ? calculateMonthlyForecast(currentData)
        : current.average.totalCost * AVERAGE_HOURS_IN_MONTH,
      prevCost:
        isDefaultFilter && previous.total.cost
          ? calculateMonthlyForecast(previousData)
          : undefined,
    },

    avgDailyCost: {
      cost: current.average.totalCost * 24,
      prevCost:
        isDefaultFilter &&
        current.average.totalCost &&
        previous.average.totalCost
          ? previous.average.totalCost * 24
          : undefined,
    },

    avgDailyCostPerCpu: toBig(current.average.normalizedCostPerCpu)
      .mul(HOURS_IN_DAY)
      .toNumber(),
    avgDailyCostPerGib: toBig(current.average.normalizedCostPerGib)
      .mul(HOURS_IN_DAY)
      .toNumber(),
    avgDailyCostPerGpu: toBig(current.average.normalizedCostPerGpu)
      .mul(HOURS_IN_DAY)
      .toNumber(),
    avgDailyCostPerStorage: toBig(current.average.costPerStorage)
      .mul(HOURS_IN_DAY)
      .toNumber(),
  };
};
